// Phase 1A (Founder-First UX) -- the new product shell.
//
// Six destinations, in the order a founder uses Martian:
//   Briefing -> Decisions -> Work -> Results -> Your Business -> Evidence
// plus an expandable INTELLIGENCE group right after Briefing (the specialist
// AI-search / search / commerce / paid / creative / market surfaces; see
// intelligenceNav.js) and Ask Martian, available everywhere (header button,
// Ctrl/Cmd+K).
//
// Laws applied here:
//   Hick     -- 6 destinations instead of 33; specialist pages live under
//               Evidence or inside the objects they explain.
//   Zeigarnik/Von Restorff -- the ONLY badges are real outstanding actions
//               (decisions that need you, work ready for review).
//   Jakob    -- left rail + workspace switcher at the top (Slack/Linear),
//               bottom tab bar on mobile (iOS/Android), account menu at the
//               bottom-left, Ctrl/Cmd+K for the assistant.
import { Fragment, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  House, Signpost, Tray, ChartLineUp, Buildings, MagnifyingGlassPlus, DotsThree,
  CaretUpDown, CaretDown, Binoculars, Check, Gear, SignOut, Sun, Moon, X, Lock,
} from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { GLIM, MartianWordmark } from '@/brand';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { TalkToMartianPanel } from '@/components/TalkToMartianPanel';
import { ConversationScopeContext } from '@/hooks/useSharedConversationState';
import { FounderWorkspaceProvider, useFounderWorkspace, useFounderPath, attentionCounts } from './FounderWorkspace';
import { toast } from 'sonner';
import { FREE_PLAN_LOCKED_PATHS } from '@/components/DashboardLayout';
import {
  INTELLIGENCE_GROUPS, intelligenceItemActive, isIntelligencePath, isIntelligenceItemLocked, LOCKED_NAV_TOAST,
} from './intelligenceNav';

export const FOUNDER_NAV = [
  { key: 'briefing', to: '/', label: 'Briefing', icon: House, end: true },
  { key: 'decisions', to: '/decisions', label: 'Decisions', icon: Signpost },
  { key: 'work', to: '/work', label: 'Work', icon: Tray },
  { key: 'results', to: '/results', label: 'Results', icon: ChartLineUp },
  { key: 'business', to: '/business', label: 'Your Business', icon: Buildings },
  { key: 'evidence', to: '/evidence', label: 'Evidence', icon: MagnifyingGlassPlus },
];
const MOBILE_PRIMARY = ['briefing', 'decisions', 'work', 'results'];

const focusRing = 'focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]';

function Badge({ count, testId }) {
  if (!count) return null;
  return (
    <span data-testid={testId} className="hidden lg:flex ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--accent)] text-white text-[11px] font-semibold tabular-nums flex items-center justify-center" aria-label={`${count} need you`}>
      {count}
    </span>
  );
}

function WorkspaceSwitcher() {
  const { clients, workspaceId, setWorkspaceId, displayName, fixedClientId } = useFounderWorkspace();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const canSwitch = !fixedClientId && clients.length > 1;
  const initial = (displayName || '?').charAt(0).toUpperCase();
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid="workspace-switcher" disabled={!canSwitch} onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox" aria-expanded={open}
        className={`w-full flex items-center gap-2.5 rounded-[var(--radius-button)] px-2 py-2 text-left ${canSwitch ? 'hover:bg-[var(--canvas-tertiary)]' : 'cursor-default'} ${focusRing}`}>
        <span className="w-8 h-8 shrink-0 rounded-[8px] bg-[var(--accent-light)] text-[var(--accent)] font-display font-bold text-[14px] flex items-center justify-center">{initial}</span>
        <span className="min-w-0 flex-1 hidden lg:block">
          <span className="block text-[13.5px] font-semibold text-[var(--text-primary)] truncate" data-testid="workspace-name">{displayName || 'Workspace'}</span>
          <span className="block text-[11.5px] text-[var(--text-tertiary)] truncate">{fixedClientId ? 'Viewing as admin' : 'Workspace'}</span>
        </span>
        {canSwitch && <CaretUpDown size={14} className="text-[var(--text-tertiary)] hidden lg:block" />}
      </button>
      {open && (
        <ul role="listbox" className="absolute z-50 left-0 right-0 mt-1 py-1 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface-raised)] shadow-[var(--shadow-dropdown)]">
          {clients.map((c) => (
            <li key={c.id}>
              <button type="button" role="option" aria-selected={c.id === workspaceId}
                onClick={() => { setWorkspaceId(c.id); setOpen(false); }}
                className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-left text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)]">
                <span className="flex-1 truncate">{c.display_name || c.name}</span>
                {c.id === workspaceId && <Check size={14} className="text-[var(--accent)]" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AccountMenu() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const item = `w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-left text-[var(--text-primary)] hover:bg-[var(--canvas-tertiary)] ${focusRing}`;
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid="account-menu" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}
        className={`w-full flex items-center gap-2.5 rounded-[var(--radius-button)] px-2 py-2 hover:bg-[var(--canvas-tertiary)] ${focusRing}`}>
        <span className="w-7 h-7 shrink-0 rounded-full bg-[var(--canvas-tertiary)] text-[var(--text-secondary)] text-[12px] font-semibold flex items-center justify-center">
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </span>
        <span className="min-w-0 flex-1 text-left hidden lg:block">
          <span className="block text-[12.5px] font-medium text-[var(--text-primary)] truncate">{user?.name || 'Account'}</span>
        </span>
      </button>
      {open && (
        <div role="menu" className="absolute z-50 bottom-full left-0 w-56 mb-1 py-1 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface-raised)] shadow-[var(--shadow-dropdown)]">
          <button role="menuitem" type="button" className={item} onClick={() => { setOpen(false); navigate('/settings'); }}><Gear size={15} /> Settings</button>
          <button role="menuitem" type="button" className={item} onClick={toggleTheme}>{theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />} {theme === 'dark' ? 'Light theme' : 'Dark theme'}</button>
          <button role="menuitem" type="button" className={item} data-testid="founder-logout" onClick={() => { logout(); navigate('/login'); }}><SignOut size={15} /> Sign out</button>
        </div>
      )}
    </div>
  );
}

// INTELLIGENCE -- the specialist AI-search / search / commerce / paid /
// creative / market surfaces (founder/intelligenceNav.js). Progressive
// disclosure: collapsed by default, opens itself on one of its pages.
function IntelligenceList({ onNavigate, testIdPrefix = 'intelligence-link' }) {
  const location = useLocation();
  const { user } = useAuth();
  return (
    <div className="space-y-2" data-testid="intelligence-list">
      {INTELLIGENCE_GROUPS.map((g) => (
        <div key={g.key} data-testid={`intelligence-group-${g.key}`}>
          <p className="px-3 mt-1 mb-0.5 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">{g.label}</p>
          {g.items.map((item) => {
            const active = intelligenceItemActive(item, location.pathname);
            // Free plan: the destination stays listed but shows the
            // established lock and explains itself instead of navigating
            // (same convention as the legacy sidebar's locked items).
            if (isIntelligenceItemLocked(item, user, FREE_PLAN_LOCKED_PATHS)) {
              return (
                <button key={item.key} type="button" data-testid={`${testIdPrefix}-${item.key}`} data-locked="true"
                  aria-disabled="true" title={LOCKED_NAV_TOAST}
                  onClick={() => toast(LOCKED_NAV_TOAST, { description: item.label })}
                  className={`w-full flex items-center h-9 px-3 rounded-[var(--radius-button)] text-[13.5px] text-left text-[var(--text-secondary)] opacity-60 cursor-default ${focusRing}`}>
                  <span className="flex-1">{item.label}</span>
                  <Lock size={12} className="ml-2 shrink-0 text-[var(--text-tertiary)]" aria-hidden="true" data-testid={`${testIdPrefix}-${item.key}-lock`} />
                  <span className="sr-only"> (locked: unlocks in your full Martian workspace)</span>
                </button>
              );
            }
            return (
              <NavLink key={item.key} to={item.to} onClick={onNavigate} data-testid={`${testIdPrefix}-${item.key}`}
                className={`flex items-center h-9 px-3 rounded-[var(--radius-button)] text-[13.5px] ${active ? 'bg-[var(--accent-light)] text-[var(--text-primary)] font-semibold' : 'text-[var(--text-secondary)] hover:bg-[var(--canvas-tertiary)] hover:text-[var(--text-primary)]'} ${focusRing}`}>
                {item.label}
              </NavLink>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function IntelligenceNav() {
  const location = useLocation();
  const active = isIntelligencePath(location.pathname);
  const [open, setOpen] = useState(active);
  const [sheetOpen, setSheetOpen] = useState(false);
  useEffect(() => { if (active) setOpen(true); }, [active]);
  // >= lg the rail is wide enough to expand in place; on the icon-only md
  // rail the same list opens in a side sheet instead.
  const wide = () => typeof window.matchMedia !== 'function' || window.matchMedia('(min-width: 1024px)').matches;
  const onToggle = () => { if (wide()) setOpen((v) => !v); else setSheetOpen(true); };
  return (
    <div data-testid="founder-nav-intelligence-group">
      <button type="button" data-testid="founder-nav-intelligence" aria-expanded={open} aria-haspopup="true" onClick={onToggle} title="Intelligence"
        className={`w-full flex items-center justify-center lg:justify-start gap-3 h-10 px-3 rounded-[var(--radius-button)] text-[14px] ${active ? 'bg-[var(--accent-light)] text-[var(--text-primary)] font-semibold' : 'text-[var(--text-secondary)] hover:bg-[var(--canvas-tertiary)] hover:text-[var(--text-primary)]'} ${focusRing}`}>
        <Binoculars size={18} weight={active ? 'fill' : 'regular'} className={active ? 'text-[var(--accent)]' : ''} />
        <span className="hidden lg:inline">Intelligence</span>
        <CaretDown size={12} className={`hidden lg:block ml-auto text-[var(--text-tertiary)] transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="hidden lg:block mt-1 mb-2 pl-3 ml-3 border-l border-[var(--border)]"><IntelligenceList /></div>}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="left" className="lg:hidden w-[280px] bg-[var(--surface)] overflow-y-auto">
          <SheetHeader className="text-left">
            <SheetTitle>Intelligence</SheetTitle>
            <SheetDescription className="sr-only">AI search, search, commerce, paid, creative and market intelligence</SheetDescription>
          </SheetHeader>
          <div className="mt-3"><IntelligenceList onNavigate={() => setSheetOpen(false)} testIdPrefix="intelligence-sheet-link" /></div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Rail() {
  const { summary, fixedClientId } = useFounderWorkspace();
  const fp = useFounderPath();
  const counts = attentionCounts(summary);
  return (
    <aside className="hidden md:flex md:w-[72px] lg:w-[240px] flex-col shrink-0 border-r border-[var(--border)] bg-[var(--surface)] sticky top-0 h-screen" data-testid="founder-rail">
      <div className="flex items-center justify-center lg:justify-start gap-2 px-4 h-14">
        <GLIM size={28} decorative />
        <span className="sr-only lg:hidden">Martian</span>
        <MartianWordmark size="sm" className="hidden lg:inline" />
      </div>
      <div className="px-2 pb-3"><WorkspaceSwitcher /></div>
      <nav aria-label="Primary" className="flex-1 px-2 space-y-0.5" data-testid="founder-nav">
        {FOUNDER_NAV.map(({ key, to, label, icon: Icon, end }) => {
          const count = key === 'decisions' ? counts.decisions : key === 'work' ? counts.work : 0;
          return (
            <Fragment key={key}>
              <NavLink to={fp(to)} end={end} data-testid={`founder-nav-${key}`}
                className={({ isActive }) => `flex items-center justify-center lg:justify-start gap-3 h-10 px-3 rounded-[var(--radius-button)] text-[14px] ${isActive ? 'bg-[var(--accent-light)] text-[var(--text-primary)] font-semibold' : 'text-[var(--text-secondary)] hover:bg-[var(--canvas-tertiary)] hover:text-[var(--text-primary)]'} ${focusRing}`}
                title={label}>
                {({ isActive }) => (
                  <>
                    <Icon size={18} weight={isActive ? 'fill' : 'regular'} className={isActive ? 'text-[var(--accent)]' : ''} />
                    <span className="hidden lg:inline">{label}</span>
                    <Badge count={count} testId={`founder-nav-${key}-count`} />
                  </>
                )}
              </NavLink>
              {/* INTELLIGENCE sits second, right after Briefing. Not shown
                  inside an admin's /workspace/:clientId view: the specialist
                  pages are not workspace-scoped. */}
              {key === 'briefing' && !fixedClientId && <IntelligenceNav />}
            </Fragment>
          );
        })}
      </nav>
      <div className="px-2 py-3 border-t border-[var(--border)]"><AccountMenu /></div>
    </aside>
  );
}

function MobileTabBar() {
  const { summary, fixedClientId } = useFounderWorkspace();
  const fp = useFounderPath();
  const counts = attentionCounts(summary);
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const more = FOUNDER_NAV.filter((n) => !MOBILE_PRIMARY.includes(n.key));
  const moreActive = more.some((n) => location.pathname.startsWith(fp(n.to))) || (!fixedClientId && isIntelligencePath(location.pathname));
  const tab = (isActive) => `flex-1 flex flex-col items-center justify-center gap-0.5 h-14 text-[11px] ${isActive ? 'text-[var(--accent)] font-semibold' : 'text-[var(--text-secondary)]'} ${focusRing}`;
  return (
    <>
      <nav aria-label="Primary" className="md:hidden fixed bottom-0 inset-x-0 z-40 flex border-t border-[var(--border)] bg-[var(--surface)] pb-[env(safe-area-inset-bottom)]" data-testid="founder-tabbar">
        {FOUNDER_NAV.filter((n) => MOBILE_PRIMARY.includes(n.key)).map(({ key, to, label, icon: Icon, end }) => {
          const count = key === 'decisions' ? counts.decisions : key === 'work' ? counts.work : 0;
          return (
            <NavLink key={key} to={fp(to)} end={end} className={({ isActive }) => tab(isActive)} data-testid={`founder-tab-${key}`}>
              {({ isActive }) => (
                <>
                  <span className="relative">
                    <Icon size={22} weight={isActive ? 'fill' : 'regular'} />
                    {count > 0 && <span className="absolute -top-1 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[var(--accent)] text-white text-[10px] font-semibold flex items-center justify-center">{count}</span>}
                  </span>
                  {label}
                </>
              )}
            </NavLink>
          );
        })}
        <button type="button" onClick={() => setMoreOpen(true)} className={tab(moreActive)} data-testid="founder-tab-more" aria-haspopup="dialog">
          <DotsThree size={22} weight="bold" />More
        </button>
      </nav>
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="md:hidden rounded-t-[16px] pb-8 bg-[var(--surface)]">
          <SheetHeader className="text-left">
            <SheetTitle>More</SheetTitle>
            <SheetDescription className="sr-only">Other places in Martian</SheetDescription>
          </SheetHeader>
          <div className="mt-3 space-y-1 max-h-[70vh] overflow-y-auto">
            <WorkspaceSwitcher />
            {more.map(({ key, to, label, icon: Icon }) => (
              <NavLink key={key} to={fp(to)} onClick={() => setMoreOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 h-12 px-3 rounded-[var(--radius-button)] text-[15px] ${isActive ? 'bg-[var(--accent-light)] font-semibold' : 'text-[var(--text-primary)]'}`}>
                <Icon size={20} /> {label}
              </NavLink>
            ))}
            {!fixedClientId && (
              <div className="pt-2" data-testid="founder-tabbar-intelligence">
                <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">Intelligence</p>
                <IntelligenceList onNavigate={() => setMoreOpen(false)} testIdPrefix="intelligence-more-link" />
              </div>
            )}
            <NavLink to="/settings" onClick={() => setMoreOpen(false)} className="flex items-center gap-3 h-12 px-3 rounded-[var(--radius-button)] text-[15px] text-[var(--text-primary)]"><Gear size={20} /> Settings</NavLink>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

// Ask Martian -- one conversation per decision (ConversationScope), right
// sheet on desktop, full screen on mobile. No credentials, ever (Phase 0 P0.9).
function AskMartianSheet() {
  const { askOpen, setAskOpen, askContext, setAskContext, workspaceId, displayName } = useFounderWorkspace();
  const scope = askContext?.entityId ? `${askContext.entityType || 'item'}:${askContext.entityId}` : '';
  return (
    <Sheet open={askOpen} onOpenChange={setAskOpen}>
      <SheetContent side="right" data-testid="ask-martian-sheet"
        className="w-full sm:max-w-[440px] h-full p-0 flex flex-col bg-[var(--surface)] [&>button]:hidden">
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3 border-b border-[var(--border)]">
          <SheetHeader className="text-left space-y-1">
            <SheetTitle className="font-display text-[17px]">Ask Martian</SheetTitle>
            <SheetDescription className="text-[12.5px] text-[var(--text-secondary)]" data-testid="ask-martian-context-label">
              {askContext?.entityLabel ? <>About: <span className="text-[var(--text-primary)]">{askContext.entityLabel}</span></> : `About ${displayName || 'your business'}`}
            </SheetDescription>
          </SheetHeader>
          <button type="button" onClick={() => setAskOpen(false)} aria-label="Close" className={`p-2 -mr-2 rounded-[var(--radius-button)] text-[var(--text-secondary)] hover:bg-[var(--canvas-tertiary)] ${focusRing}`}>
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          <ConversationScopeContext.Provider value={scope}>
            <TalkToMartianPanel clientId={workspaceId || null} context={askContext} onClearContext={() => setAskContext(null)} />
          </ConversationScopeContext.Provider>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function useAskShortcut() {
  const { setAskOpen } = useFounderWorkspace();
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setAskOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setAskOpen]);
}

function ShellBody({ children }) {
  useAskShortcut();
  const { fixedClientId, displayName } = useFounderWorkspace();
  const navigate = useNavigate();
  return (
    <div className="founder-shell min-h-screen flex bg-[var(--canvas-secondary)] text-[var(--text-primary)]" data-testid="founder-shell">
      <a href="#founder-main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:bg-[var(--surface)] focus:px-3 focus:py-2 focus:rounded">Skip to content</a>
      <Rail />
      <main id="founder-main" className="flex-1 min-w-0 pb-24 md:pb-12" data-testid="main-content">
        {fixedClientId && (
          <div className="px-5 md:px-10 py-2 bg-[var(--accent)] text-white text-[12.5px] flex items-center justify-between gap-3" data-testid="workspace-context-banner">
            <span>Viewing workspace: <strong>{displayName}</strong></span>
            <button type="button" className="font-semibold underline-offset-4 hover:underline" onClick={() => navigate('/')} data-testid="back-to-admin-workspace">Back to Admin Workspace</button>
          </div>
        )}
        <div className="mx-auto w-full max-w-[1040px] px-5 md:px-10 pt-6 md:pt-10">{children}</div>
      </main>
      <MobileTabBar />
      <AskMartianSheet />
    </div>
  );
}

export function FounderShell({ children, fixedClientId, basePath = '' }) {
  return (
    <FounderWorkspaceProvider fixedClientId={fixedClientId} basePath={basePath}>
      <ShellBody>{children}</ShellBody>
    </FounderWorkspaceProvider>
  );
}
